/**
 * MediaEmbedPlayer - the in-app play affordance for a music/video link.
 *
 * Audio always renders its native player straight away (it is cheap - no
 * network fetch happens until the listener actually presses play, thanks to
 * `preload="none"`). Video and provider embeds (YouTube, Drive, Vimeo) stay
 * click-to-load: the row shows a Play button first, and only mounts the
 * `<video>` or `<iframe>` after the tap. That keeps a class with a long
 * media list from mounting a video decoder or an iframe per row on page
 * load - the exact page-load cost the rest of the app just got tightened
 * up for.
 *
 * MOST callers keep their own "open in a new tab" link alongside this
 * component - this only ever adds a play option, it does not have to be the
 * only escape hatch. The dancer surfaces are the deliberate exception:
 * Bobbie, 2026-08-31, ruled the dancer media row down to exactly two
 * controls, play and download, and dropped the third "open in a new tab"
 * link entirely - see components/dancer/DancerMediaRow.tsx's own header for
 * why. For a link this component genuinely cannot play (see the `broken`
 * state below), THAT caller has nothing left to fall back to but honest
 * text, which is why this component itself now says so rather than staying
 * silent when `broken` is true.
 *
 * THE PLAY BUTTON'S VISIBLE TEXT IS GENERIC ("Play"), NOT THE TITLE. Every
 * caller (FamilyVideos, MediaPanel, DancerMediaRow) already prints the
 * link's own label in its own row, directly above where this mounts - so a
 * button whose own on-screen text ALSO repeated that label put the same
 * string in the DOM twice for one row. PR #809 review caught this as a
 * concrete regression: `page.getByText(label)` in
 * tests/dancer-younger-doors.spec.ts resolved to two elements (the row's own
 * label AND "Play <label>") and failed strict mode. `title`, when given,
 * still becomes the button's `aria-label` - a screen reader landing
 * directly on the button (no surrounding row read first) still hears the
 * full "Play <title>", it is just no longer ALSO a second literal text node
 * next to the row's own label.
 *
 * TRY AGAIN (PR #809 review round 8, Codex). Bobbie is usually on a weak
 * mobile connection, and `broken` was TERMINAL: one transient network
 * hiccup on an otherwise perfectly good, always-worked-before file
 * permanently replaced the player with failure text for the rest of the
 * session, with no way back short of a remount (a different item, or a
 * page reload). The Try again button resets `broken` AND `loaded` back to
 * their fresh-mount values - the SAME state a brand-new item starts in -
 * so the user sees exactly the Play button (or, for audio, the native
 * player) they had before anything failed, and can deliberately reattempt
 * on their own terms. Deliberately NOT an automatic retry: no timer, no
 * loop, one tap asks for exactly one fresh attempt, and an honest failure
 * again shows the same message and the same button again - never a hang,
 * never a silent no-op. `onRetry` (optional) tells the CALLER a retry was
 * requested, so a row tracking its own "this item is unplayable" state
 * (DancerMediaRow.tsx's unplayableItemId, which feeds its escape hatch) can
 * clear it - the hatch and the retry button are complementary, not
 * duplicates: retry is for "try again, it might have been transient", the
 * hatch is for "this genuinely does not work here at all". Without
 * clearing that tracked state on retry, the hatch would keep showing
 * beside a Try again button that just proved it might not be needed.
 *
 * SPEED (Bobbie, 2026-10-14: a staff member asked for "a .75 and a .85
 * speed", and Bobbie asked "Is that doable to speed up videos?"). A direct
 * audio or video link gets the shared Speed control (0.75x, 0.85x, Normal)
 * right under its player: under the native audio player, and under a
 * video's Play button before the tap as well as under the video after it,
 * so a speed picked before the first play is already in force when the
 * element mounts. The speed is this instance's own state: it stays through
 * a re-signed link to the SAME file (only the url's query changed, a new
 * Storage token or R2 presign) and through Try again, and it starts at
 * Normal on every fresh mount. Like loaded and broken below, it resets on
 * its own, with no key needed from the caller: a url naming a DIFFERENT
 * file (playbackItemKey: origin and path) puts it back to Normal, so a
 * speed picked for one item never carries onto another. It is NOT shown
 * for a YouTube, Vimeo or Drive iframe (the Hub cannot set another site's
 * player speed; YouTube has its own under its gear), nor while the item is
 * showing its could-not-play message. See components/PlaybackSpeedControl.
 */
import { useEffect, useState, type SyntheticEvent } from 'react';
import { classifyMediaUrlWithHint, unplayableVideoReason, type MediaKindHint } from '../lib/mediaEmbed';
import { playbackItemKey } from '../lib/playbackSpeed';
import { PlaybackSpeedControl, usePlaybackSpeed } from './PlaybackSpeedControl';

const playButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  minHeight: 'var(--tap-target)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--line)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontWeight: 800,
  fontSize: 13,
  fontFamily: 'var(--font-body)',
  cursor: 'pointer',
};

const audioStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 'var(--tap-target)',
  display: 'block',
};

// The player and its Speed control, stacked. minWidth 0 so a long track
// never pushes a narrow row wider than the phone.
const withSpeedStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
  minWidth: 0,
};

const videoStyle: React.CSSProperties = {
  width: '100%',
  maxHeight: 320,
  borderRadius: 'var(--radius-md)',
  background: '#000',
  display: 'block',
};

// 16:9 keeps every provider's iframe from collapsing to zero height. The
// wrapper's own width still comes from the caller's layout.
const iframeWrapStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  paddingTop: '56.25%',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
  background: '#000',
};

const iframeStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  border: 0,
};

const couldNotPlayStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: 'var(--ink-soft)',
  padding: '4px 0',
};

// For a url the classifier flatly cannot embed here (unplayableVideoReason),
// as opposed to the `broken` state above, which is for a hinted url that
// mounted a real element and then genuinely failed to load.
const unplayableNoteStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-soft)',
  padding: '4px 0',
};

const couldNotPlayWrapStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
};

// Same visual family as the Play button on purpose (Try again occupies the
// exact spot Play would, once broken is cleared) - full width, the same
// 44px floor via var(--tap-target) in playButtonStyle, just spread over it
// rather than duplicated.
const retryButtonStyle: React.CSSProperties = {
  ...playButtonStyle,
};

/**
 * Minimal permission set for a playback iframe. `allow-scripts` and
 * `allow-same-origin` let the PROVIDER's own player run inside its OWN
 * origin (youtube.com / drive.google.com / vimeo.com) - it says nothing
 * about our origin, because sandboxed same-origin only ever refers to the
 * framed document's own resource origin. `allow-presentation` covers
 * fullscreen/cast controls the players offer. Nothing else is granted: no
 * top-navigation, no downloads, no popups, no forms.
 */
const IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-presentation';
const IFRAME_ALLOW = 'autoplay; encrypted-media; picture-in-picture; fullscreen';

export function MediaEmbedPlayer({
  url,
  title,
  kindHint,
  onCannotPlay,
  onRetry,
  onCannotPlayOriginal,
  copyFailed = false,
}: {
  url: string;
  title: string;
  /**
   * What the caller already knows this link is (its own stored `kind`, or
   * which of musicLinks/videoLinks it came from) - independent of the URL's
   * file extension. Only matters when the URL alone classifies as `none`:
   * a signed GCS/R2 playback URL carries no file extension at all, so
   * without this a known video or music link with such a URL rendered NO
   * player, silently, and the caller's own "open in a new tab" link became
   * the only affordance - the exact download-reads-as-the-only-option shape
   * this component exists to avoid. See classifyMediaUrlWithHint.
   */
  kindHint?: MediaKindHint;
  /**
   * Fired when this url genuinely cannot be played here - immediately (the
   * classifier, even with kindHint, still says `none`: a provider-page link
   * it knows it cannot embed, like a Drive folder or a malformed YouTube
   * link) or later (a real `onError`: a hinted url that turned out not to be
   * playable media at all - a Dropbox share page, a Loom page, anything a
   * kindHint optimistically pointed a `<video>`/`<audio>` tag at that was
   * never actually raw media bytes). Optional - most callers keep their own
   * "open in a new tab" escape hatch regardless and do not need this.
   * DancerMediaRow.tsx uses it, alongside its own upfront classification
   * check, to decide whether ITS OWN narrow escape hatch should appear - see
   * there. May fire more than once; callers should treat it as "still true",
   * not count invocations.
   */
  onCannotPlay?: () => void;
  /**
   * Fired when the Try again button (the `broken` state's own retry
   * control) is pressed - not when a retry SUCCEEDS or fails, just when
   * one was requested. Optional. DancerMediaRow.tsx uses it to clear its
   * own tracked unplayableItemId, so its escape hatch stops assuming this
   * item is unplayable the moment the user asks to try again - the hatch
   * and the retry button stay complementary rather than both showing at
   * once for no reason. If the retry fails too, onCannotPlay fires again on
   * its own and the row's tracked state (and the hatch) come right back.
   */
  onRetry?: () => void;
  /**
   * The caller's switch to a smaller copy that plays everywhere
   * (useResolvedPlaybackUrl's playCopy). When it is set, a video that fails
   * to play, or plays its sound over a blank picture (an HEVC original on a
   * device that cannot decode it, Bobbie 2026-10-24: "doesn't play"), calls
   * it instead of showing the could-not-play note, and the player opens
   * straight onto the copy when its url arrives. Null or absent keeps the
   * behavior above exactly.
   */
  onCannotPlayOriginal?: (() => void) | null;
  /**
   * True when the copy asked for through onCannotPlayOriginal could not be
   * fetched (useResolvedPlaybackUrl's copyFailed). The player then shows the
   * could-not-play note with Try again, the same as with no copy at all.
   */
  copyFailed?: boolean;
}) {
  const embed = classifyMediaUrlWithHint(url, kindHint);
  // Speed for a direct audio or video element. Called on every render,
  // whatever the kind, so the hook order never changes; only the audio and
  // video branches below draw the control or hand over the element ref.
  // Keyed by the FILE the url names, so a different item handed to this same
  // instance starts at Normal (the same self-reset as loaded and broken).
  const playbackSpeed = usePlaybackSpeed(playbackItemKey(url));
  const [loaded, setLoaded] = useState(false);
  // Set by a genuine onError on a mounted <video>/<audio> element - ANY
  // real playback failure, not only a kindHint-forced guess that turned
  // out wrong: a perfectly ordinary, always-worked-before .mp4 with a
  // transient network hiccup on a weak connection (Bobbie's actual
  // situation, PR #809 review round 8) sets this exactly the same way.
  // That is why `broken` needs its own Try again control below rather than
  // staying a dead end - most of the time this fires, nothing was ever
  // wrong with the file at all.
  const [broken, setBroken] = useState(false);

  // SELF-RESETTING, regardless of whether the CALLER remembers to key this
  // instance. PR #809 review round 6 (Codex): FamilyVideos.tsx renders its
  // newest-item slot through VideoLinkPlayer -> MediaEmbedPlayer at a fixed,
  // UNKEYED position (VideoLinkPlayer itself carries no state of its own -
  // this component is the only place `loaded`/`broken` actually live), so
  // when a newer video replaces the newest one, React reused this instance
  // with the SAME stale carryover the dancer surfaces had before they were
  // fixed by keying their own call sites. Keying every call site was always
  // going to leave the next one exposed - this is the fix at the root
  // instead: React's own "adjusting state during render" pattern (see
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders).
  // Comparing `url` against what THIS instance rendered last render, and
  // resetting synchronously if it differs, catches the change before the
  // browser ever paints the stale player - no flash, and no effect (an
  // effect would run AFTER the stale content is already on screen for one
  // frame). Every caller, present and future, is protected without
  // remembering a key; the key={item.id} at the dancer call sites stays -
  // harmless, and it still documents the intent there.
  const [lastUrl, setLastUrl] = useState(url);
  // Set when the original could not play and the caller is fetching the
  // copy: the next url opens already playing instead of asking for Play again.
  const [openNextUrl, setOpenNextUrl] = useState(false);
  const [autoStart, setAutoStart] = useState(false);
  if (url !== lastUrl) {
    setLastUrl(url);
    setLoaded(openNextUrl);
    setAutoStart(openNextUrl);
    setBroken(false);
    if (openNextUrl) setOpenNextUrl(false);
  }

  const playAriaLabel = title ? `Play ${title}` : undefined;

  // The IMMEDIATE half of onCannotPlay: a url this component was never
  // going to embed at all, known without waiting for a play attempt.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onCannotPlay is a caller-supplied callback, not render state; including it would re-fire this effect on every caller re-render that hands in a new function identity
  useEffect(() => {
    if (embed.kind === 'none') onCannotPlay?.();
  }, [embed.kind]);

  // The DELAYED half: a hinted url that mounted a real element and then
  // genuinely failed to load.
  const reportBroken = () => {
    if (onCannotPlayOriginal) {
      if (!openNextUrl) {
        setOpenNextUrl(true);
        onCannotPlayOriginal();
      }
      return;
    }
    setBroken(true);
    onCannotPlay?.();
  };

  // A picture the device cannot decode often does not raise an error at all:
  // the sound plays over a blank box. Once the first frame is due, a video
  // with no width and no height has no picture this device can show. Only
  // checked when there is a copy to switch to.
  const checkPicture = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!onCannotPlayOriginal) return;
    const element = event.currentTarget;
    if (element.videoWidth === 0 && element.videoHeight === 0) reportBroken();
  };

  // One tap, one fresh attempt - deliberately NOT automatic and NOT a loop.
  // Resets to exactly the state a brand-new mount starts in (loaded and
  // broken both false), so the next render shows the Play button again (or,
  // for audio, the native player again) rather than silently re-attempting
  // anything on its own. If it fails again, reportBroken fires again on its
  // own and this same screen reappears - never a hang, never a silent
  // no-op.
  const handleRetry = () => {
    setBroken(false);
    setLoaded(false);
    setAutoStart(false);
    setOpenNextUrl(false);
    onRetry?.();
  };

  // The original could not play and the copy was asked for. Still the same
  // url means the copy has not arrived: say so rather than leave a dead box.
  const waitingForCopy = openNextUrl && !copyFailed;
  const copyNeverCame = openNextUrl && copyFailed;
  useEffect(() => {
    if (copyNeverCame) onCannotPlay?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the embed.kind effect above
  }, [copyNeverCame]);

  if (waitingForCopy && embed.kind === 'video') {
    return (
      <div style={couldNotPlayWrapStyle}>
        <p style={couldNotPlayStyle}>Getting a version this device can play...</p>
      </div>
    );
  }

  if (broken || copyNeverCame) {
    // "Try again" stays a literal, contiguous phrase even with a title -
    // matches the same convention Play's own aria-label uses (`Play ${title}`
    // always leads with the word "Play"), and it is what every caller and
    // test locates this button by.
    const retryAriaLabel = title ? `Try again - ${title}` : 'Try again';
    return (
      <div style={couldNotPlayWrapStyle}>
        <p style={couldNotPlayStyle}>
          {title ? `${title} could not be played here.` : 'This could not be played here.'}
        </p>
        <button type="button" style={retryButtonStyle} onClick={handleRetry} aria-label={retryAriaLabel}>
          <span aria-hidden>{'↻'}</span>
          Try again
        </button>
      </div>
    );
  }

  const speedControl = (
    <PlaybackSpeedControl speed={playbackSpeed.speed} onChoose={playbackSpeed.choose} title={title} />
  );

  if (embed.kind === 'audio') {
    return (
      <div style={withSpeedStyle}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- studio audio, no captions to carry */}
        <audio
          ref={playbackSpeed.mediaRef}
          controls
          preload="none"
          style={audioStyle}
          src={embed.playUrl}
          aria-label={title || undefined}
          onError={reportBroken}
        />
        {speedControl}
      </div>
    );
  }

  if (embed.kind === 'video') {
    return (
      <div style={withSpeedStyle}>
        {loaded ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- studio video, no captions to carry
          <video
            ref={playbackSpeed.mediaRef}
            controls
            preload="none"
            playsInline
            autoPlay={autoStart}
            style={videoStyle}
            src={embed.playUrl}
            aria-label={title || undefined}
            onError={reportBroken}
            onLoadedData={checkPicture}
          />
        ) : (
          <button
            type="button"
            style={playButtonStyle}
            onClick={() => setLoaded(true)}
            aria-label={playAriaLabel}
          >
            <span aria-hidden>{'▶'}</span>
            Play
          </button>
        )}
        {speedControl}
      </div>
    );
  }

  if (embed.kind === 'iframe') {
    if (!loaded) {
      return (
        <button
          type="button"
          style={playButtonStyle}
          onClick={() => setLoaded(true)}
          aria-label={playAriaLabel}
        >
          <span aria-hidden>{'▶'}</span>
          Play
        </button>
      );
    }
    return (
      <div style={iframeWrapStyle}>
        <iframe
          src={embed.embedUrl}
          title={title || 'Video'}
          loading="lazy"
          allow={IFRAME_ALLOW}
          allowFullScreen
          sandbox={IFRAME_SANDBOX}
          style={iframeStyle}
        />
      </div>
    );
  }

  const reason = unplayableVideoReason(url);
  if (reason) {
    return <div style={unplayableNoteStyle}>{reason}</div>;
  }

  return null;
}

/**
 * True when a link can play in the app at all (used to decide whether to
 * show the Play row). `hint` matches classifyMediaUrlWithHint - pass it
 * whenever the caller knows the kind, or an extensionless-but-playable link
 * (signed GCS/R2 playback URL) reads as un-embeddable here even though
 * MediaEmbedPlayer would actually play it given the same hint.
 */
export function canEmbedMedia(url: string, hint?: MediaKindHint): boolean {
  return classifyMediaUrlWithHint(url, hint).kind !== 'none';
}
